import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  InputBase,
  IconButton,
  Button,
  Container,
  Hidden,
  Menu,
  MenuItem,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import MenuIcon from '@material-ui/icons/Menu';
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { fetchLocationSuggestions } from '../../services/placesService';

const useStyles = makeStyles((theme) => ({
  appBar: {
    background: 'transparent',
    boxShadow: 'none',
  },
  shellWrap: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
  },
  navFrame: {
    minHeight: 74,
    borderRadius: 20,
    padding: theme.spacing(0.6, 1.2),
    background: 'linear-gradient(120deg, rgba(32, 44, 35, 0.9) 0%, rgba(63, 77, 65, 0.84) 58%, rgba(114, 91, 75, 0.84) 160%)',
    border: '1px solid rgba(255, 248, 238, 0.18)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 18px 36px rgba(36, 24, 17, 0.25)',
  },
  toolBar: {
    minHeight: 60,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: '#fff7ee',
    gap: theme.spacing(1),
    marginRight: theme.spacing(3),
    padding: theme.spacing(1.2, 1.5),
    borderRadius: 14,
    border: '1px solid rgba(255, 248, 238, 0.14)',
    background: 'rgba(255, 248, 238, 0.06)',
  },
  brandIcon: {
    fontSize: 24,
  },
  brandText: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  nav: {
    display: 'flex',
    gap: theme.spacing(0.7),
    flexGrow: 1,
    background: 'rgba(255, 247, 237, 0.08)',
    border: '1px solid rgba(255, 247, 237, 0.14)',
    borderRadius: 14,
    padding: theme.spacing(0.5),
    marginRight: theme.spacing(1.2),
  },
  navLink: {
    color: 'rgba(255, 245, 234, 0.94)',
    padding: '8px 14px',
    borderRadius: 12,
    textDecoration: 'none',
    fontWeight: 700,
    transition: 'background-color 180ms ease, color 180ms ease',
    '&.active': {
      background: 'linear-gradient(135deg, rgba(255, 246, 236, 0.27) 0%, rgba(255, 227, 198, 0.2) 100%)',
      color: '#fffefc',
      boxShadow: 'inset 0 0 0 1px rgba(255, 245, 233, 0.24)',
    },
    '&:hover': {
      backgroundColor: alpha('#fff6ed', 0.14),
    },
  },
  search: {
    position: 'relative',
    borderRadius: 14,
    backgroundColor: 'rgba(255, 248, 238, 0.12)',
    marginRight: theme.spacing(0.6),
    marginLeft: 0,
    width: 340,
    border: '1px solid rgba(255, 248, 238, 0.14)',
    transition: theme.transitions.create('background-color'),
    '&:hover': {
      backgroundColor: 'rgba(255, 248, 238, 0.18)',
    },
    [theme.breakpoints.down('sm')]: {
      width: 220,
    },
  },
  suggestions: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: 0,
    right: 0,
    borderRadius: 12,
    border: '1px solid rgba(255, 248, 238, 0.26)',
    background: 'linear-gradient(180deg, #fffdf9 0%, #f8efe3 100%)',
    boxShadow: '0 14px 26px rgba(45, 33, 24, 0.24)',
    maxHeight: 320,
    overflow: 'auto',
    zIndex: 20,
  },
  suggestionItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '10px 12px',
    border: 0,
    textAlign: 'left',
    background: 'transparent',
    cursor: 'pointer',
    color: '#2f2721',
    fontWeight: 600,
    '&:hover': {
      background: 'rgba(245, 233, 217, 0.7)',
    },
  },
  suggestMeta: {
    fontSize: '0.72rem',
    color: '#7c6b5c',
    marginLeft: 8,
  },
  suggestionState: {
    padding: '10px 12px',
    color: '#6d5d4f',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: '#fff8ef',
  },
  inputInput: {
    padding: theme.spacing(1.2, 1.2, 1.2, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
    width: '100%',
    fontWeight: 600,
    '&::placeholder': {
      opacity: 0.82,
      color: '#f5ede2',
    },
  },
  pageBody: {
    minHeight: 'calc(100vh - 74px)',
    paddingBottom: theme.spacing(8),
  },
  footer: {
    borderTop: '1px solid rgba(154, 130, 108, 0.25)',
    padding: theme.spacing(3, 0, 2.2, 0),
    marginTop: theme.spacing(6),
    color: '#5b4f45',
    background: 'linear-gradient(180deg, #fbf7f1 0%, #f3ebe0 100%)',
  },
  footerTop: {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1fr 1fr',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(2),
    },
  },
  footerBrandTitle: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    color: '#2f2721',
    marginBottom: theme.spacing(0.5),
  },
  footerHeading: {
    fontWeight: 800,
    color: '#2f2721',
    marginBottom: theme.spacing(1),
    fontSize: '0.95rem',
  },
  footerLinkRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    alignItems: 'flex-start',
  },
  footerLinkBtn: {
    padding: 0,
    minWidth: 0,
    fontWeight: 600,
    color: '#5b4f45',
    justifyContent: 'flex-start',
    '&:hover': {
      background: 'transparent',
      color: '#2f2721',
    },
  },
  footerBottom: {
    borderTop: '1px solid rgba(154, 130, 108, 0.2)',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
  },
}));

const navItems = [
  { to: '/', label: 'Home' },
  { to: '/explore', label: 'Explore' },
  { to: '/saved', label: 'Saved' },
  { to: '/itinerary', label: 'Itinerary' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

const AppShell = ({ children }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState(new URLSearchParams(location.search).get('q') || '');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const fromUrl = new URLSearchParams(location.search).get('q') || '';
    if (fromUrl !== query) setQuery(fromUrl);
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setSuggesting(false);
      return undefined;
    }

    let active = true;
    const timer = setTimeout(() => {
      setSuggesting(true);
      fetchLocationSuggestions(trimmed)
        .then((items) => {
          if (!active) return;
          setSuggestions(items);
        })
        .finally(() => {
          if (active) setSuggesting(false);
        });
    }, 280);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query]);

  const submitSearch = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    const suffix = params.toString();
    setShowSuggestions(false);
    navigate(`/explore${suffix ? `?${suffix}` : ''}`);
  };

  const handleSuggestionClick = (item) => {
    const value = item?.label || '';
    setQuery(value);
    setShowSuggestions(false);
    const params = new URLSearchParams();
    if (value.trim()) params.set('q', value.trim());
    const suffix = params.toString();
    navigate(`/explore${suffix ? `?${suffix}` : ''}`);
  };

  return (
    <>
      <AppBar position="sticky" className={classes.appBar}>
        <Container maxWidth="xl" className={classes.shellWrap}>
          <Box className={classes.navFrame}>
            <Toolbar disableGutters className={classes.toolBar}>
              <Link to="/" className={classes.brand} aria-label="Your Travel Guide home">
                <FlightTakeoffIcon className={classes.brandIcon} />
                <Typography variant="h6" className={classes.brandText}>Your Travel Guide</Typography>
              </Link>

              <Hidden smDown>
                <nav className={classes.nav} aria-label="Primary">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.to}
                      end={item.to === '/'}
                      to={item.to}
                      className={({ isActive }) => `${classes.navLink}${isActive ? ' active' : ''}`}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
              </Hidden>

              <form onSubmit={submitSearch} className={classes.search} role="search" aria-label="Global place search">
                <div className={classes.searchIcon}><SearchIcon fontSize="small" /></div>
                <InputBase
                  placeholder="Search cities, hotels, cafes..."
                  classes={{ root: classes.inputRoot, input: classes.inputInput }}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 160)}
                  inputProps={{ 'aria-label': 'Search places' }}
                />
                {showSuggestions && query.trim().length >= 2 && (
                  <div className={classes.suggestions}>
                    {suggesting && (
                      <div className={classes.suggestionState}>
                        <CircularProgress size={14} />
                        Finding locations...
                      </div>
                    )}
                    {!suggesting && suggestions.length === 0 && (
                      <div className={classes.suggestionState}>No suggestions found</div>
                    )}
                    {!suggesting && suggestions.map((item) => (
                      <button
                        key={`${item.label}-${item.lat || 'na'}-${item.lng || 'na'}`}
                        type="button"
                        className={classes.suggestionItem}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleSuggestionClick(item)}
                      >
                        <span>{item.label}</span>
                        {item.lat && item.lng && <span className={classes.suggestMeta}>lat/lng</span>}
                      </button>
                    ))}
                  </div>
                )}
              </form>

              <Hidden mdUp>
                <IconButton color="inherit" onClick={(event) => setMenuAnchor(event.currentTarget)} aria-label="Open menu">
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                  keepMounted
                >
                  {navItems.map((item) => (
                    <MenuItem
                      key={item.to}
                      selected={location.pathname === item.to}
                      onClick={() => {
                        navigate(item.to);
                        setMenuAnchor(null);
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Hidden>
            </Toolbar>
          </Box>
        </Container>
      </AppBar>

      <main className={classes.pageBody}>{children}</main>

      <footer className={classes.footer}>
        <Container maxWidth="xl">
          <Box className={classes.footerTop}>
            <Box>
              <Typography variant="h6" className={classes.footerBrandTitle}>Your Travel Guide</Typography>
              <Typography variant="body2" color="textSecondary">
                Explore, shortlist, and plan better trips with a map-first experience built for modern travelers.
              </Typography>
            </Box>
            <Box>
              <Typography className={classes.footerHeading}>Product</Typography>
              <Box className={classes.footerLinkRow}>
                <Button className={classes.footerLinkBtn} component={Link} to="/explore">Explore</Button>
                <Button className={classes.footerLinkBtn} component={Link} to="/saved">Saved Places</Button>
                <Button className={classes.footerLinkBtn} component={Link} to="/itinerary">Itinerary</Button>
              </Box>
            </Box>
            <Box>
              <Typography className={classes.footerHeading}>Company</Typography>
              <Box className={classes.footerLinkRow}>
                <Button className={classes.footerLinkBtn} component={Link} to="/about">About</Button>
                <Button className={classes.footerLinkBtn} component={Link} to="/faq">FAQ</Button>
                <Button className={classes.footerLinkBtn} component={Link} to="/contact">Contact</Button>
              </Box>
            </Box>
          </Box>
          <Box className={classes.footerBottom} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="textSecondary">© {new Date().getFullYear()} Your Travel Guide. All rights reserved.</Typography>
            <Typography variant="caption" color="textSecondary">Built for smarter travel planning.</Typography>
          </Box>
        </Container>
      </footer>
    </>
  );
};

export default AppShell;
